import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield } from 'lucide-react'
import { Header } from '../components/landing/Header'
import { Footer } from '../components/landing/Footer'
import { useAuth } from '../contexts/AuthContext'

export function PrivacyPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-bg flex flex-col overflow-x-hidden">
      <Header
        isAuthenticated={!!user}
        onDashboardClick={() => navigate(user ? '/dashboard' : '/login')}
        onSignUpClick={() => navigate('/signup')}
      />
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6">
      <div className="container-custom max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-text-muted hover:text-accent transition-colors text-sm mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-accent-2">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-text">
                Privacy <span className="text-gradient">Policy</span>
              </h1>
              <p className="text-text-muted text-sm mt-0.5">Last updated: March 11, 2026</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">1. Introduction</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              Softitect ("we", "us", or "our") operates the Softitect AI platform, a scalable API service for accessing AI language models. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website and services. By using Softitect AI, you agree to the practices described in this policy.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">2. Information We Collect</h2>

            <h3 className="text-sm font-semibold text-text mb-2 mt-4">Account Information</h3>
            <p className="text-text-muted text-sm leading-relaxed mb-2">
              When you register for an account, we collect:
            </p>
            <ul className="space-y-2 text-sm text-text-muted mb-4">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Email address (used as your primary identifier)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>First name, last name, and company name (optional)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Intended use case or usage description (optional)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Account credentials managed securely through our identity provider</li>
            </ul>

            <h3 className="text-sm font-semibold text-text mb-2">API Usage Data</h3>
            <p className="text-text-muted text-sm leading-relaxed mb-2">
              When you use the API, we automatically collect:
            </p>
            <ul className="space-y-2 text-sm text-text-muted mb-4">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>API request metadata (model ID, timestamp, token counts, response times)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Credit and usage statistics associated with your account</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>API key identifiers (not the key values themselves)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Error logs and diagnostic information for service reliability</li>
            </ul>

            <h3 className="text-sm font-semibold text-text mb-2">Billing Information</h3>
            <p className="text-text-muted text-sm leading-relaxed mb-2">
              Subscription and payment processing is handled by Paddle. We do not store your full payment card details. We receive from Paddle:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Subscription status, tier, and billing period dates</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Paddle subscription and customer IDs for account management</li>
            </ul>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">3. How We Use Your Information</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Provision and operate your account and API access</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Authenticate your identity and authorize API requests</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Track usage against your subscription tier and enforce rate limits</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Process subscription payments and manage billing events via Paddle webhooks</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Send transactional communications (account confirmations, billing receipts, service alerts)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Detect and prevent fraud, abuse, and unauthorized access</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Improve the reliability and performance of the Service</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Comply with legal obligations</li>
            </ul>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">4. Data Storage and Infrastructure</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              Your data is stored and processed using secure cloud infrastructure. We apply encryption at rest and in transit across all data stores, and our infrastructure is operated by reputable cloud service providers that maintain industry-standard compliance certifications.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">5. Information Sharing and Disclosure</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              We do not sell your personal information. We may share your data only in the following circumstances:
            </p>

            <h3 className="text-sm font-semibold text-text mb-2 mt-4">Service Providers</h3>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              We share data with trusted third-party providers who help us operate the Service, including:
            </p>
            <ul className="space-y-2 text-sm text-text-muted mb-4">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Paddle</span> - payment processing and subscription management</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Cloud infrastructure providers</span> - hosting, compute, and data storage</span></li>
            </ul>

            <h3 className="text-sm font-semibold text-text mb-2">Legal Requirements</h3>
            <p className="text-text-muted text-sm leading-relaxed">
              We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect the rights, property, or safety of Softitect, our users, or the public.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">6. Data Security</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              We implement industry-standard technical and organizational measures to protect your data:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>All data in transit is encrypted using TLS/HTTPS</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Data at rest is encrypted across all storage services</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Authentication is handled by our identity provider with secure token management</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Billing webhook events are verified using HMAC SHA-256 signature validation</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>API keys are managed with strict access controls at the infrastructure level</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Access to production systems is restricted to authorized personnel only</li>
            </ul>
            <p className="text-text-muted text-sm leading-relaxed mt-3">
              While we take reasonable precautions, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">7. Data Retention</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide the Service. Usage logs and API request metadata may be retained for up to 12 months for billing verification, fraud prevention, and service improvement purposes. Upon account deletion, we will delete or anonymize your personal data within a reasonable timeframe, except where retention is required by law or for legitimate business purposes such as resolving disputes or enforcing agreements.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">8. Your Rights and Choices</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul className="space-y-2 text-sm text-text-muted mb-3">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Access</span> - request a copy of the personal data we hold about you</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Correction</span> - update inaccurate or incomplete information via your account profile</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Deletion</span> - request deletion of your account and associated personal data</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Portability</span> - request your data in a structured, machine-readable format</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Objection</span> - object to certain types of processing, such as direct marketing</span></li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span><span><span className="text-text">Restriction</span> - request that we limit processing of your data in certain circumstances</span></li>
            </ul>
            <p className="text-text-muted text-sm leading-relaxed">
              To exercise any of these rights, contact us at <a href="mailto:support@softitect.com" className="text-accent hover:text-accent-2 transition-colors">support@softitect.com</a>. We will respond within 30 days.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">9. Cookies and Tracking</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              Our web application uses session-based authentication tokens managed by our identity provider. We use minimal cookies and local storage strictly necessary for authentication and session management. We do not use third-party advertising cookies or behavioral tracking technologies. Any analytics we perform are based on aggregated, anonymized usage data.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">10. International Data Transfers</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              Our infrastructure is hosted across secure cloud data centers, which may process and store data in multiple geographic regions. By using the Service, you consent to the transfer of your information to these facilities. We take appropriate steps to ensure that any international transfers of personal data are conducted in accordance with applicable data protection laws.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">11. Children's Privacy</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              The Service is intended for users who are at least 18 years of age or the age of majority in their jurisdiction. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete such information promptly.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">12. Changes to This Policy</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of material changes by posting the updated policy on this page and updating the "Last updated" date. For significant changes, we may also send an email notification to your registered address. Your continued use of the Service after any changes constitutes your acceptance of the updated policy.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">13. Contact Us</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="p-4 rounded-lg bg-bg border border-border">
              <p className="text-sm text-text-muted">
                Email: <a href="mailto:support@softitect.com" className="text-accent hover:text-accent-2 transition-colors">support@softitect.com</a>
              </p>
            </div>
          </div>

        </div>
      </div>
      </main>
      <Footer />
    </div>
  )
}
